/**
 * The wallet session, as a plain observable store.
 *
 * Nothing here imports an adapter package, react or preact. The store holds state and
 * hands work to the registry, which is code-split behind a dynamic import — so a page that
 * merely *offers* a wallet ships no wallet code. That is `site-ux.md` §5.1 read as a bundle
 * requirement: the wallet is asked for when Pay is pressed, and until then it is not even
 * downloaded.
 *
 * `window.tronWeb` appears nowhere. Detection, signing and the network check all go through
 * the adapters.
 */
import { emitWalletConnectUri } from "./connect-uri.js";
import { classifyWalletError, WalletFailure } from "./errors.js";
import { tronNetwork } from "./network.js";
/** Remembered so the modal can say "last used"; it never triggers a connection. */
const LAST_WALLET_KEY = "tenergy.wallet.last";
const EMPTY = {
    wallets: [],
    address: null,
    walletId: null,
    connecting: false,
    connectingWalletId: null,
    lastWalletId: null,
    modalOpen: false,
    error: null,
    loadingRuntime: false,
};
export class WalletController {
    snapshot = EMPTY;
    listeners = new Set();
    bundles = null;
    loading = null;
    active = null;
    waiters = [];
    loadRegistry;
    constructor(options = {}) {
        this.loadRegistry = options.loadRegistry;
        // localStorage is read here rather than during render: the server has no idea what
        // this browser used, and rendering the badge during hydration would be a mismatch.
        this.patch({ lastWalletId: readLastWallet() });
    }
    getSnapshot = () => this.snapshot;
    subscribe = (listener) => {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    };
    openModal = () => {
        this.patch({ error: null, modalOpen: true });
        void this.ensureBundles();
    };
    closeModal = () => {
        this.patch({ modalOpen: false });
        this.settle(null);
    };
    requestConnection = () => {
        const address = this.snapshot.address;
        if (address)
            return Promise.resolve(address);
        this.openModal();
        return new Promise((resolve, reject) => {
            this.waiters.push({ resolve, reject });
        });
    };
    readyState = (walletId) => this.snapshot.wallets.find((entry) => entry.id === walletId)?.readyState ?? "loading";
    connect = async (walletId) => {
        this.patch({ error: null, connecting: true, connectingWalletId: walletId });
        try {
            const bundles = await this.ensureBundles();
            let bundle = bundles.find((entry) => entry.descriptor.id === walletId);
            if (!bundle)
                throw new WalletFailure("unavailable", walletId);
            // Trust outside its own in-app browser has no injected provider; the relay is the
            // only way to reach it, and the row is hidden when there is no relay.
            if (bundle.descriptor.walletConnectFallback &&
                stateOf(bundle.adapter) === "NotFound") {
                const relay = bundles.find((entry) => entry.descriptor.id === "WalletConnect");
                if (!relay)
                    throw new WalletFailure("not_installed", walletId);
                bundle = relay;
            }
            let adapter = bundle.adapter;
            if (!adapter) {
                adapter = await bundle.load();
                bundle.adapter = adapter;
                this.watch(adapter);
            }
            await adapter.connect(bundle.descriptor.id === "WalletConnect"
                ? { onUri: emitWalletConnectUri }
                : undefined);
            // No network check here: signing in is a message signature, the same on every TRON network
            // (owner 2026-10-03: a mainnet wallet must be able to sign in to the Nile cabinet). The chain
            // is checked where it matters, before a transaction is signed (signAndSendTrx).
            const address = adapter.address;
            if (!address)
                throw new WalletFailure("locked", walletId);
            this.active = adapter;
            rememberLastWallet(walletId);
            this.patch({
                address,
                walletId,
                lastWalletId: walletId,
                modalOpen: false,
                connecting: false,
                connectingWalletId: null,
            });
            this.refreshEntries();
            this.settle(address);
            return address;
        }
        catch (cause) {
            const failure = classifyWalletError(cause, walletId);
            this.patch({ error: failure, connecting: false, connectingWalletId: null });
            throw failure;
        }
    };
    /**
     * Reconnect the wallet this browser used last, WITHOUT opening anything and without
     * throwing.
     *
     * Only the dashboard calls this, and only once a session already exists: the person has
     * signed in, so they have already approved this site in this wallet, and a page that then
     * asks them to press `Connect` again before they can top up is asking twice for the same
     * answer. A marketing page must never call it — arriving on a landing page has approved
     * nothing, and `site-ux.md` §5.1's rule is that the wallet is asked for on a press.
     *
     * A wallet that is locked, uninstalled or on another chain simply stays disconnected: the
     * failure is swallowed rather than surfaced, because nobody asked for this to happen.
     */
    reconnect = async () => {
        if (this.snapshot.address)
            return this.snapshot.address;
        const walletId = this.snapshot.lastWalletId;
        if (!walletId)
            return null;
        try {
            return await this.connect(walletId);
        }
        catch {
            this.patch({ error: null });
            return null;
        }
    };
    disconnect = async () => {
        const adapter = this.active;
        this.active = null;
        this.patch({ address: null, walletId: null, error: null });
        this.refreshEntries();
        if (adapter)
            await adapter.disconnect().catch(() => undefined);
    };
    signMessage = async (text) => {
        const adapter = this.active;
        if (!adapter)
            throw new WalletFailure("locked", null, "No wallet is connected");
        try {
            return await adapter.signMessage(text);
        }
        catch (cause) {
            const failure = classifyWalletError(cause, this.snapshot.walletId);
            this.patch({ error: failure });
            throw failure;
        }
    };
    signAndSendTrx = async (input) => {
        const adapter = this.active;
        const from = adapter?.address ?? null;
        if (!adapter || !from) {
            throw new WalletFailure("locked", this.snapshot.walletId, "No wallet is connected");
        }
        const network = tronNetwork();
        // Loaded here rather than at the top: the transfer builder is only ever needed once a
        // wallet has already been connected, so it belongs in the wallet chunk.
        const { broadcastTransaction, buildTrxTransfer } = await import("./trx-transfer.js");
        try {
            // A wallet on another chain would sign a transfer for the wrong network: refuse before building it.
            await assertExpectedNetwork(adapter, this.snapshot.walletId);
            const unsigned = await buildTrxTransfer({
                fullHost: network.fullHost,
                from,
                to: input.to,
                amountSun: input.amountSun,
                memo: input.memo,
            });
            const signed = (await adapter.signTransaction(unsigned));
            return await broadcastTransaction({ fullHost: network.fullHost, transaction: signed });
        }
        catch (cause) {
            const failure = classifyWalletError(cause, this.snapshot.walletId);
            this.patch({ error: failure });
            throw failure;
        }
    };
    /**
     * Sign a transaction our API built (a wallet payment, owner 2026-10-06) and broadcast exactly those bytes: the
     * receiver is bound to their txid, so nothing may be rebuilt here. Only the connected wallet's own transfers.
     */
    signAndSendPrepared = async (transaction) => {
        const adapter = this.active;
        if (!adapter || !adapter.address) {
            throw new WalletFailure("locked", this.snapshot.walletId, "No wallet is connected");
        }
        const network = tronNetwork();
        const { broadcastTransaction } = await import("./trx-transfer.js");
        try {
            await assertExpectedNetwork(adapter, this.snapshot.walletId);
            const signed = (await adapter.signTransaction(transaction));
            return await broadcastTransaction({ fullHost: network.fullHost, transaction: signed });
        }
        catch (cause) {
            const failure = classifyWalletError(cause, this.snapshot.walletId);
            this.patch({ error: failure });
            throw failure;
        }
    };
    // ---- internals
    ensureBundles() {
        if (this.bundles)
            return Promise.resolve(this.bundles);
        if (this.loading)
            return this.loading;
        this.patch({ loadingRuntime: true });
        const load = this.loadRegistry ?? (async () => (await import("./registry.js")).loadAdapterRegistry());
        this.loading = Promise.resolve()
            .then(load)
            .then((bundles) => {
            this.bundles = bundles;
            for (const bundle of bundles)
                if (bundle.adapter)
                    this.watch(bundle.adapter);
            this.patch({ loadingRuntime: false });
            this.refreshEntries();
            return bundles;
        }, () => {
            this.bundles = [];
            this.patch({ loadingRuntime: false });
            this.refreshEntries();
            return this.bundles;
        });
        return this.loading;
    }
    /**
     * An adapter tells us when it has finished looking for its wallet — and, for the one in use,
     * when its account changes or it goes away (owner 2026-09-27: the site did not notice a
     * disconnected wallet). TronLink reports a lock as `accountsChanged("")` plus `disconnect`,
     * the same as revoking the site, so both only clear the address: the adapter stays active,
     * and unlocking it (`accountsChanged(address)`) brings the address back with no new prompt.
     * What a changed address means for a signed-in session is `useDashboardSession`'s call.
     */
    watch(adapter) {
        const onChange = () => this.refreshEntries();
        adapter.on("stateChanged", onChange);
        adapter.on("connect", onChange);
        adapter.on("disconnect", () => {
            onChange();
            if (adapter === this.active)
                this.patch({ address: null });
        });
        adapter.on("accountsChanged", (address) => {
            if (adapter === this.active)
                this.patch({ address: address || null });
        });
    }
    refreshEntries() {
        const bundles = this.bundles ?? [];
        this.patch({ wallets: buildEntries(bundles) });
    }
    settle(address, cause) {
        const waiting = this.waiters.splice(0, this.waiters.length);
        for (const waiter of waiting) {
            if (address)
                waiter.resolve(address);
            else
                waiter.reject(cause ?? new WalletFailure("connection_closed"));
        }
    }
    patch(next) {
        this.snapshot = { ...this.snapshot, ...next };
        for (const listener of this.listeners)
            listener();
    }
}
function stateOf(adapter) {
    return adapter ? (adapter.state ?? null) : null;
}
function readyLabel(bundle) {
    if (!bundle.adapter)
        return bundle.probe?.() ?? "loading";
    switch (stateOf(bundle.adapter)) {
        case "Loading":
            return "loading";
        case "NotFound":
            return "not-found";
        default:
            return "detected";
    }
}
/** Detected browser wallets first, then the transports, then what is not there. */
function rank(entry) {
    if (entry.readyState === "detected" && entry.kind === "browser")
        return 0;
    if (entry.readyState === "not-found")
        return 2;
    return 1;
}
export function buildEntries(bundles) {
    const hasRelay = bundles.some((bundle) => bundle.descriptor.id === "WalletConnect");
    return bundles
        .map((bundle) => ({ bundle, readyState: readyLabel(bundle) }))
        .filter(({ bundle, readyState }) => !(bundle.descriptor.walletConnectFallback && readyState === "not-found" && !hasRelay))
        .map(({ bundle, readyState }) => ({
        id: bundle.descriptor.id,
        kind: bundle.descriptor.kind,
        install: bundle.descriptor.install,
        icon: bundle.adapter?.icon ?? null,
        readyState,
    }))
        .sort((left, right) => rank(left) - rank(right));
}
/**
 * A wallet pointed at another chain would sign a transfer that goes nowhere we can see, so a
 * transaction is refused (the wallet stays connected: switching its network is enough). Only the
 * injected adapters can be asked; a relay session is scoped to the chain it was opened for.
 */
async function assertExpectedNetwork(adapter, walletId) {
    const probe = adapter;
    if (typeof probe.network !== "function")
        return;
    let chainId;
    try {
        chainId = (await probe.network())?.chainId;
    }
    catch {
        // A node that will not answer is not evidence of the wrong chain.
        return;
    }
    const expected = tronNetwork();
    if (!chainId || chainId.toLowerCase() === expected.chainId)
        return;
    throw new WalletFailure("wrong_network", walletId, expected.label);
}
function readLastWallet() {
    try {
        return globalThis.localStorage?.getItem(LAST_WALLET_KEY) ?? null;
    }
    catch {
        // A browser with storage denied simply has no memory of a last wallet.
        return null;
    }
}
function rememberLastWallet(walletId) {
    try {
        globalThis.localStorage?.setItem(LAST_WALLET_KEY, walletId);
    }
    catch {
        // Remembering is a convenience; a browser that refuses storage still connects.
    }
}
//# sourceMappingURL=controller.js.map